// @flow
import traverse from '@babel/traverse';
import * as t from '@babel/types';
import { componentNames, mathSymbols } from '../../../index.js';
import type { DocType, LocatableError } from '../types.js';
import {
  getComponentWhitelistForDocType,
  usageRestrictionsForComponent,
  getComponentWhitelistForEditingEnvironment,
  type EditingEnvironment,
} from './componentWhitelist.js';

export const docTypes: DocType[] = [
  'RetrofittedStudentLesson',
  'RetrofittedStudentLessonTeacherNote',
  'Worksheet',
  'AnswerKey',
  'PrimaryTeacherNote',
  'Playground',
  'EngageActivity',
  'ActivityTeacherNote',
  'SolidifyLesson',
  'SolidifyTeacherNote',
  'TopicOverview',
  'TopicAssessment',
  'TopicAnswerKey',
  'TeacherSubtopicOverview',
  'StudentSubtopicOverview',
  'EngageActivityNote',
  'Investigation',
  'InvestigationTeacherNote',
  'Glossary',
];

const unrestrictedAstTypes = [
  // JSX node types
  'JSXText',
  'JSXElement',
  'JSXOpeningElement',
  'JSXClosingElement',
  'JSXIdentifier',
  'JSXAttribute',
  'JSXExpressionContainer',
  'JSXEmptyExpression',
  // 'JSXSpreadAttribute', (not allowed)
  // 'JSXFragment', (not allowed)
  // 'JSXOpeningFragment', (not allowed)
  // 'JSXClosingFragment', (not allowed)
  // 'JSXMemberExpression', (not allowed)

  // JS types
  'BooleanLiteral',
  'NumericLiteral',
  'StringLiteral',
  'TemplateLiteral',
  'TemplateElement',
  'ArrayExpression',
  'ObjectExpression',
];

// Restricted AST types can be used, but only under particular circumstances.
const restrictedAstTypes = [
  'UnaryExpression', // only want unary minus: eg. -3
  'ObjectProperty', // Non-computed only
  'Identifier', // Only Infinity

  'JSXFragment', // only at root in non-document contexts
  'JSXOpeningFragment', // only at root in non-document contexts
  'JSXClosingFragment', // only at root in non-document contexts
];

const allowedAstTypes = [...unrestrictedAstTypes, ...restrictedAstTypes];

export function validateAstForDocument(
  ast: Object,
): null | Array<LocatableError> {
  const rootStructureErrors = validateWellFormedRootStructure({
    ast,
    allowFragmentAtRoot: false,
  });
  if (rootStructureErrors !== null) return rootStructureErrors;

  const rootStatement = ast.program.body[0];
  const rootElementName = rootStatement.expression.openingElement.name.name;

  if (docTypes.includes(rootElementName) === false) {
    return [
      errorWithoutSensibleLocation(
        `The document must begin with one of these elements: ${docTypes
          .map((el) => `<${el}>`)
          .join(', ')}.`,
      ),
    ];
  }

  const whitelistedComponentNames =
    getComponentWhitelistForDocType(rootElementName);
  return validateJsx({
    rootJsxExpressionStatement: rootStatement,
    whitelistedComponentNames,
    validationContext: {
      kind: 'document',
      name: rootElementName,
    },
  });
}

export function validateAstForEditingEnvironment(
  ast: Object,
  editingEnvironment: EditingEnvironment,
): null | Array<LocatableError> {
  const rootStructureErrors = validateWellFormedRootStructure({
    ast,
    allowFragmentAtRoot: true,
  });
  if (rootStructureErrors !== null) return rootStructureErrors;

  return validateJsx({
    rootJsxExpressionStatement: ast.program.body[0],
    whitelistedComponentNames:
      getComponentWhitelistForEditingEnvironment(editingEnvironment),
    validationContext: {
      kind: 'editingEnvironment',
      name: editingEnvironment,
    },
  });
}

// The AST is any arbitrary javascript AST. Here we interrogate the root
// area of the program's ast to ensure it has the characteristics of a
// singly-rooted JSX expression. None of our other validation make sense
// unless we are working with a JSX expression, so we'll bail out early
// if this produces errors.
function validateWellFormedRootStructure({
  ast,
  allowFragmentAtRoot,
}): null | Array<LocatableError> {
  const { body } = ast.program;
  if (body.length !== 1) {
    return [
      errorWithoutSensibleLocation(
        'JSX is malformed. The program body contains more than 1 node',
      ),
    ];
  }
  const rootStatement = body[0];
  if (rootStatement.type !== 'ExpressionStatement') {
    return [
      errorWithoutSensibleLocation(
        'JSX is malformed. Root statement is not an ExpressionStatement',
      ),
    ];
  }
  const rootElement = rootStatement.expression;
  if (allowFragmentAtRoot) {
    if (
      rootElement.type !== 'JSXElement' &&
      rootElement.type !== 'JSXFragment'
    ) {
      return [
        errorWithoutSensibleLocation(
          'JSX is malformed. Root element is neither a JSXElement, nor a JSXFragment',
        ),
      ];
    }
  } else {
    if (rootElement.type !== 'JSXElement') {
      return [
        errorWithoutSensibleLocation(
          'JSX is malformed. Root element is not a JSXElement',
        ),
      ];
    }
  }

  return null;
}

function validateJsx({
  rootJsxExpressionStatement,
  whitelistedComponentNames,
  validationContext,
}: {|
  rootJsxExpressionStatement: Object,
  whitelistedComponentNames: Array<string>,
  validationContext:
    | {| kind: 'document', name: DocType |}
    | {| kind: 'editingEnvironment', name: EditingEnvironment |},
|}): null | Array<LocatableError> {
  const validationErrors = [];

  // NOTE the traversal does not visit the node with which you begin the traversal. Thus,
  // traversing from the root jsx expression statement will mean the first visited node is
  // the root element. This is the root node of the JSX element which we want the following
  // checks to apply.
  traverse(rootJsxExpressionStatement, {
    // This is required since we aren't starting traversal at the File/Program level.
    noScope: true,
    enter(path) {
      const { node } = path;

      // Disallow AST nodes that we have not explicitly whitelisted.
      if (allowedAstTypes.includes(node.type) === false) {
        validationErrors.push({
          message: `${node.type} not allowed. We only want to express simple data in our written content.`,
          ...toMarkerDataLocationFormat(node.loc),
        });
      }

      // NOTE we don't have any explicit checks for the restricted AST types
      // 'JSXOpeningFragment' and 'JSXClosingFragment' since they can only exist
      // as properties on the JSXFragment AST node, so will implicitly be
      // allowed/disallowed based upon this check. Having 3 overlapping error
      // messages in the case of a failure is not helpful, given that they all
      // come together as an atomic unit.
      if (node.type === 'JSXFragment') {
        if (
          validationContext.kind === 'editingEnvironment' &&
          (validationContext.name === 'diagnosticQuestion' ||
            validationContext.name === 'diagnosticQuestionDraft' ||
            validationContext.name === 'diagnosticQuestionPublication')
        ) {
          // JSXFragment can be used at the root in a diagnostic question
          if (path.parentPath !== null) {
            validationErrors.push({
              message: 'A fragment (<></>) can only be used at the root',
              ...toMarkerDataLocationFormat(node.loc),
            });
          }
        } else {
          // JSXFragment is banned in any other validation context
          validationErrors.push({
            message: 'Fragments (<></>) are not allowed',
            ...toMarkerDataLocationFormat(node.loc),
          });
        }
      }

      // UnaryExpression is only allowed for
      // a) expressing negative numbers
      // b) expressing -Infinity
      // TODO add a test that asserts that -Infinity is valid
      if (
        node.type === 'UnaryExpression' &&
        !(
          node.operator === '-' &&
          (node.argument.type === 'NumericLiteral' ||
            (node.argument.type === 'Identifier' &&
              node.argument.name === 'Infinity'))
        )
      ) {
        validationErrors.push({
          message: 'UnaryExpression not allowed',
          ...toMarkerDataLocationFormat(node.loc),
        });
      }

      if (node.type === 'ObjectProperty' && node.computed) {
        validationErrors.push({
          message: `Computed object properties are not allowed`,
          ...toMarkerDataLocationFormat(node.loc),
        });
      }

      // Identifier is only allowed for non-computed property names in objects, or for Infinity.
      if (node.type === 'Identifier') {
        const computedProperty =
          path.parent.type === 'ObjectProperty' &&
          path.parent.computed === true;
        const nonComputedProperty =
          path.parent.type === 'ObjectProperty' &&
          path.parent.computed === false;

        if (
          computedProperty ||
          (!nonComputedProperty && node.name !== 'Infinity')
        ) {
          validationErrors.push({
            message: `The identifier \`${node.name}\` is not allowed.`,
            ...toMarkerDataLocationFormat(node.loc),
          });
        }
      }

      // Only allow JSXIdentifier's for our custom set of components
      if (node.type === 'JSXElement') {
        const name = node.openingElement.name.name;
        // TODO we only have to do this check because we don't shortcircuit
        // our errors. If we had bailed out earlier when a MemberExpression
        // was used, then we don't have to do this.
        if (
          node.openingElement.name.type === 'JSXIdentifier' &&
          whitelistedComponentNames.includes(name) === false
        ) {
          let message;
          if (componentNames.includes(name)) {
            if (validationContext.kind === 'document') {
              message = `The JSX element <${name}> is not allowed in ${
                validationContext.name
              } documents. It can only be used in ${usageRestrictionsForComponent(
                name,
              ).allowedIn.join(', ')}`;
            } else {
              // TODO this errors message is a bit sloppy due to camel-cased EditingEnvironment name
              message = `The JSX element <${name}> is not allowed in a ${validationContext.name} context`;
            }
          } else {
            message = `The JSX element <${name}> is not allowed. See docs for allowed JSX.`;
          }

          validationErrors.push({
            message,
            ...toMarkerDataLocationFormat(node.loc),
            extra: {
              errorVariant: 'AST',
              variantType: 'DISALLOWED_COMPONENT',
              nodeName: name,
            },
          });
        }
      }

      validationForBadJsxUsage(path, validationErrors);

      // The f prop for FunctionPlot gets to play by its own rules. It is the one place
      // where non-data constructs can be used. We will stop the main visitor recursion
      // here, and start a new recursion with new rules specifically for what is allowed
      // within the expression container of f (mainly simple mathematical expressions/functions)
      // <FunctionPlot f={x => ...} />
      if (t.isJSXAttribute(node) && node.name.name === 'f') {
        path.skip();
        const valuePath = path.get('value');

        // NOTE: we will rely upon the spec checks to give an error message for this case
        // but we still want to bail out from recursing as any further errors will be spurious.
        if (!t.isJSXExpressionContainer(valuePath.node)) return;

        const { expression } = valuePath.node;

        if (
          (t.isArrowFunctionExpression(expression, {
            async: false, // Must not be an async function
            generator: false, // Must not be a generator function
          }) &&
            expression.params.length === 1 &&
            t.isIdentifier(expression.params[0], { name: 'x' })) === false
        ) {
          validationErrors.push({
            message:
              'Must provide a function expression similar to this: f={x => x + 2}',
            ...toMarkerDataLocationFormat(expression.loc),
          });
          return;
        }

        // Start a new traversal of the expression
        const visitor = {
          noScope: true,
          enter(path) {
            const { node } = path;

            // Allow numbers
            if (t.isNumericLiteral(node)) return;

            // Don't validate identifiers that are the callee of a call expression when we visit
            // that identifier. We instead will validate these particular identifiers in the
            // CallExpression branch where house all the function call errors checks/messages.
            if (
              t.isIdentifier(node) &&
              t.isCallExpression(path.parent) &&
              path.parent.callee === node
            )
              return;

            // Allow the function variable x, and the builtin math constants PI and E
            if (t.isIdentifier(node)) {
              const allowedIdentifiers = ['x', 'PI', 'E'];
              if (allowedIdentifiers.includes(node.name) === false) {
                validationErrors.push({
                  message: [
                    `You tried to use the identifier: ${node.name}`,
                    `You can only use these identifiers: ${allowedIdentifiers.join(
                      ' ',
                    )}`,
                  ].join('\n'),
                  ...toMarkerDataLocationFormat(node.loc),
                });
              }
              return;
            }

            // Allow unary minus
            if (t.isUnaryExpression(node)) {
              if (node.operator !== '-') {
                validationErrors.push({
                  message: [
                    `You tried to use the unary operator: ${node.operator}`,
                    `You can only use unary minus. Eg. -3`,
                  ].join('\n'),
                  ...toMarkerDataLocationFormat(node.loc),
                });
              }

              return;
            }

            // Allow binary addition, subtraction, multiplication, division, exponentiation
            if (t.isBinaryExpression(node)) {
              const allowedBinaryOperators = ['+', '-', '*', '/', '**'];
              if (allowedBinaryOperators.includes(node.operator) === false) {
                validationErrors.push({
                  message: [
                    `You tried to use the binary operator: ${node.operator}`,
                    `You can only use these binary operators: ${allowedBinaryOperators.join(
                      ' ',
                    )}`,
                  ].join('\n'),
                  ...toMarkerDataLocationFormat(node.loc),
                });
              }
              return;
            }

            // Allow call expressions if the callee is a simple identifier.
            // We will restrict the identifier to our set of math functions in
            // the next if statement.
            if (t.isCallExpression(node)) {
              // If we aren't using a simple identifier to call then we're doing something wrong.
              if (t.isIdentifier(node.callee) === false) {
                validationErrors.push({
                  message: [
                    `You have tried to call a disallowed function, or in an invalid way`,
                    `You can only use these functions: ${mathSymbols.join(
                      ' ',
                    )}`, // TODO this will include PI/E which we dont want
                  ].join('\n'),
                  ...toMarkerDataLocationFormat(node.loc),
                });
                // Don't traverse into the call expression. The whole thing is bogus.
                path.skip();
                return;
              }
              // Here we validate that you have used the correct arity for the function

              const funcName = node.callee.name;
              // TODO make this an "exahustive pattern matching" switch with flow
              switch (funcName) {
                // Functions with arity 1
                case 'abs':
                case 'sin':
                case 'cos':
                case 'tan':
                case 'asin':
                case 'acos':
                case 'atan':
                case 'degToRad': {
                  if (node.arguments.length !== 1) {
                    validationErrors.push({
                      message: `${funcName} must take 1 argument. Eg. ${funcName}(x)`,
                      ...toMarkerDataLocationFormat(node.loc),
                    });
                  }
                  break;
                }

                // Functions with arity 2
                case 'root':
                case 'log': {
                  if (node.arguments.length !== 2) {
                    validationErrors.push({
                      message: `${funcName} must take 2 arguments. Eg. ${funcName}(2, x)`,
                      ...toMarkerDataLocationFormat(node.loc),
                    });
                  }
                  break;
                }

                default:
                  validationErrors.push({
                    message: [
                      `You tried to use the function: ${funcName}`,
                      `You can only use the functions: ${mathSymbols.join(
                        ' ',
                      )}`, // TODO this will include PI/E which we dont want
                    ].join('\n'),
                    ...toMarkerDataLocationFormat(node.loc),
                  });
              }

              return;
            }

            // If we get here then we have hit some particular AST node type that is not allowed
            // just give generic message as there are too many possibilities to give tailored support
            validationErrors.push({
              message: [
                `You haven't defined a well-formed math function such as f={x => x + 2}`,
                `You have used a ${node.type} which is not allowed`,
                `See the docs for detailed info on how to define these functions`,
              ].join('\n'),
              ...toMarkerDataLocationFormat(node.loc),
            });
          },
        };
        valuePath.get('expression').traverse(visitor);
      }
    },
  });
  return validationErrors.length > 0 ? validationErrors : null;
}

// TODO figure out a better name for this.
// What I want to convey is we have 2 different type of AST validation
// - One type is for restricting the JS that can be used.
// - The other is for defending against stupid patterns that we see content authors writing.
function validationForBadJsxUsage(path, validationErrors) {
  validateCharactersInText(path, validationErrors);
  validateNoAdjacentLatex(path, validationErrors);
}

const r = String.raw;
// prettier-ignore
const allowedChars = [
  `a-zA-Z0-9`,  // english alphanumeric set
  r`\(\)`,      // parentheses
  r`\/`,        // forwards slash
  `'"“”‘’`,     // quotation marks
  r`;:,\.\-?`,  // general punctuation
  `&`,          // fancy symbols
  `⬚`,          // We use this for placeholder in latex
  r` \n`,       // Whitespace

  // Spanish orthography
  'ñÑ',         // extra letter in the alphabet
  'áéíóúÁÉÍÓÚ', // acute accents for vowels
  'üÜ',         // diaeresis for u
  'ýÝ',         // pre-modern spanish acute accent (names have it sometimes)
  '¿',          // for questions (ie. interrogative sentences)
  `«»`,         // quotation marks

  // Haitian creole orthography (required in Florida)
  'àèòÀÈÒ',     // grave accents for vowels

  // Portugues orthography
  'áéíóúÁÉÍÓÚ', // acute accents for vowels
  'àèìòùÀÈÌÒÙ', // grave accents for vowels
  'âêôÂÊÔ',     // circumflex accents for vowels
  'ãõÃÕ',       // tilde for vowels
  'çÇ',         // cedilla for c
  `'"“”‘’«»—`,  // quotation marks

  // Music notation
  // These are very niche, but someone needed to write about music notes
  '♭♯',         // flat and sharp
].join('');

const illegalCharsRe = new RegExp(`(?<char>[^${allowedChars}])`, 'g');

function validateCharactersInText(path, validationErrors) {
  if (path.node.type === 'JSXText') {
    const text = path.node.value;
    const matches = [...text.matchAll(illegalCharsRe)];
    if (matches.length > 0) {
      for (const match of matches) {
        const { index, groups } = match;
        const { line, column } = path.node.loc.start;
        const linesInNode = text.slice(0, index).split('\n');
        const lastLine = linesInNode[linesInNode.length - 1];
        const colAdjustment = linesInNode.length === 1 ? column : 0; // On the first line, we need to start at the AST node col.

        validationErrors.push({
          message: `This character is not in our allowed list: ${groups.char}. Speak to us if you think it should be.`,
          startLineNumber: line + linesInNode.length - 1,
          startColumn: colAdjustment + lastLine.length + 1,
          endLineNumber: line + linesInNode.length - 1,
          endColumn: colAdjustment + lastLine.length + 2,
        });
      }
    }
  }
}

function validateNoAdjacentLatex(path, validationErrors) {
  if (isLatexElement(path)) {
    const prev = priorNonWhitespaceSibling(path);
    if (isLatexElement(prev)) {
      validationErrors.push({
        message:
          'Adjacent <Latex /> components should not be used. They should be combined into a single expression.',
        ...toMarkerDataLocationFormat(path.node.loc),
      });
    }
  }
}

function isLatexElement(path) {
  return (
    t.isJSXElement(path.node) &&
    t.isJSXIdentifier(path.node.openingElement.name) &&
    path.node.openingElement.name.name === 'Latex'
  );
}

function priorNonWhitespaceSibling(path) {
  // See https://github.com/babel/babel/blob/master/packages/babel-traverse/src/path/family.js
  const cur = path.getPrevSibling();
  if (!cur) return undefined;

  // This case: <Latex />   <Latex />
  if (t.isJSXText(cur.node) && cur.node.value.trim() === '') {
    return priorNonWhitespaceSibling(cur);
  }

  // This case: <Latex />{' '}<Latex />
  if (
    t.isJSXExpressionContainer(cur.node) &&
    t.isStringLiteral(cur.node.expression) &&
    cur.node.expression.value.trim() === ''
  ) {
    return priorNonWhitespaceSibling(cur);
  }

  return cur;
}

// Converts the Babylon location format to Monaco's IMarkerData location format.
function toMarkerDataLocationFormat(location) {
  return {
    startLineNumber: location.start.line,
    startColumn: location.start.column + 1,
    endLineNumber: location.end.line,
    endColumn: location.end.column + 1,
  };
}

// We want all our validation errors to have associated location data
// so we can show content authors inline error messaging. There are, however,
// some (admittedly unlikely) errors in the AST validation that don't map
// sensibly to code ranges. For these we still want to provide a similar
// error reporting UX for the content authors so we synthesize location data
// for the error so the first line of the source code is highlighted.
function errorWithoutSensibleLocation(message: string): LocatableError {
  return {
    message,
    startLineNumber: 1,
    startColumn: 0,
    endLineNumber: 1,
    endColumn: 80,
  };
}
